'use client';

import { TypographyP2, TypographyPBold } from '@heidi/design-system/typography';
import { Loader2 } from 'lucide-react';
import * as m from 'motion/react-m';
import Markdown from 'react-markdown';

import type { EnhancedLine } from './types';

const LAYOUT_EASE = [0.4, 0, 0.2, 1] as const;

/**
 * Pill rendered while a phase of the preview is still resolving (e.g.
 * "Rewriting with abbreviations…", "Creating Treatment Plan…"). Shared by
 * the abbreviation-swap view and the main MicTestPreview body.
 */
export function StatusPill({ label }: { label: string }) {
  return (
    <m.div
      layoutId="status-pill"
      className="my-1 flex w-full items-center gap-2.5 rounded-xl bg-surface-accent px-4 py-2.5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        layout: { duration: 0.7, ease: LAYOUT_EASE },
        opacity: { duration: 0.2 },
      }}
    >
      <Loader2 className="size-4 animate-spin text-accent-forest" />
      <TypographyPBold className="text-accent-forest">{label}</TypographyPBold>
    </m.div>
  );
}

/**
 * Renders a single line of markdown inline — strips the wrapping `<p>` so
 * the result composes inside our typography components, but preserves
 * inline emphasis (`**bold**`, `*italic*`, `` `code` ``) from the API note.
 */
const INLINE_MARKDOWN_COMPONENTS = {
  p: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-text-primary">{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => (
    <em className="italic">{children}</em>
  ),
  code: ({ children }: { children?: React.ReactNode }) => (
    <code className="rounded bg-background-secondary px-1 py-0.5 text-xs">
      {children}
    </code>
  ),
};

export function InlineMarkdown({ text }: { text: string }) {
  return <Markdown components={INLINE_MARKDOWN_COMPONENTS}>{text}</Markdown>;
}

/**
 * Renders a list of parsed note lines (header / paragraph / bullet) with
 * inline markdown emphasis preserved. Used by the cross-fade reveal in the
 * editing phase of the MicTest preview.
 */
export function EnhancedContent({ lines }: { lines: EnhancedLine[] }) {
  return (
    <div className="flex flex-col gap-1">
      {lines.map((line, i) => {
        if (line.type === 'header') {
          return (
            <TypographyPBold key={i} className="mt-3 first:mt-0">
              {line.text}
            </TypographyPBold>
          );
        }
        if (line.type === 'paragraph') {
          return (
            <TypographyP2 key={i} className="text-text-secondary">
              <InlineMarkdown text={line.text} />
            </TypographyP2>
          );
        }
        return (
          <TypographyP2 key={i} className="text-text-secondary">
            &bull; <InlineMarkdown text={line.text} />
          </TypographyP2>
        );
      })}
    </div>
  );
}
